import React, { useState, useEffect } from 'react';
import { User, School, GeneratedCredential, AuditLog, TeacherAccount } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import AdminStatCard from '../ui/AdminStatCard';
import ResetPasswordConfirmationModal from '../modals/ResetPasswordConfirmationModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import { api } from '../../utils/api';
import useDebounce from '../../hooks/useDebounce';

const initials = (a?: string, b?: string) => `${(a || '?')[0]}${(b || '')[0] || ''}`.toUpperCase();

const Avatar: React.FC<{ a?: string; b?: string; color: string }> = ({ a, b, color }) => (
  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${color}`}>
    {initials(a, b)}
  </div>
);

const EmptyState: React.FC<{ icon: string; message: string }> = ({ icon, message }) => (
  <div className="py-12 text-center">
    <div className="text-4xl mb-2">{icon}</div>
    <p className="text-gray-500">{message}</p>
  </div>
);

const ACTION_COLOR: Record<string, string> = {
    DELETE: 'bg-red-100 text-red-700',
    RESET: 'bg-orange-100 text-orange-700',
    APPROVE: 'bg-green-100 text-green-700',
    DECLINE: 'bg-red-100 text-red-700',
    CREATE: 'bg-blue-100 text-blue-700',
    GRADE: 'bg-purple-100 text-purple-700',
};
const actionColor = (action: string) => {
    const key = Object.keys(ACTION_COLOR).find(k => action.toUpperCase().includes(k));
    return key ? ACTION_COLOR[key] : 'bg-gray-100 text-gray-700';
};

const AuditLogViewer: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({
                    page: page.toString(),
                    limit: '20',
                    search: debouncedSearchTerm,
                });
                const { logs: fetchedLogs, totalPages: newTotalPages } = await api.get<{ logs: AuditLog[], totalPages: number }>(`/api/v1/superadmin/audit-logs?${params}`);
                setLogs(fetchedLogs);
                setTotalPages(newTotalPages);
            } catch (err) {
                if (err instanceof Error) setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [page, debouncedSearchTerm]);

    return (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                <span className="text-xl">📜</span>
                <h3 className="text-lg font-semibold text-gray-800">System Audit Log</h3>
            </div>
            <div className="p-6 pb-0">
                <div className="relative max-w-xs">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
                    <input
                        type="text"
                        placeholder="Search by user email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>
            </div>
            <div className="p-6">
            {loading ? <LoadingSpinner /> : error ? <p className="text-red-500">{error}</p> : (
                <>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {logs.length > 0 ? logs.map(log => (
                                    <tr key={log.log_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(log.created_at).toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.user_email}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold font-mono ${actionColor(log.action_type)}`}>{log.action_type}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.target_type && `${log.target_type} (${log.target_id?.substring(0,8)}...)`}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{log.details?.ip || 'N/A'}</td>
                                    </tr>
                                )) : (
                                     <tr><td colSpan={5} className="p-0"><EmptyState icon="📭" message="No logs found." /></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex justify-between items-center">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 bg-gray-100 rounded-md font-medium hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-gray-100">&larr; Previous</button>
                        <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 bg-gray-100 rounded-md font-medium hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-gray-100">Next &rarr;</button>
                    </div>
                </>
            )}
            </div>
        </div>
    );
};


interface SuperAdminDashboardProps {
  user: User;
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ user }) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);
  const [teachers, setTeachers] = useState<TeacherAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'schools' | 'admins' | 'teachers' | 'audit'>('schools');

  const [newSchoolName, setNewSchoolName] = useState("");
  const [newAdminFirstName, setNewAdminFirstName] = useState("");
  const [newAdminLastName, setNewAdminLastName] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [newAdminSchoolId, setNewAdminSchoolId] = useState("");

  const [resetCredential, setResetCredential] = useState<GeneratedCredential | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'school' | 'teacher', data: School | TeacherAccount } | null>(null);

  const [formFeedback, setFormFeedback] = useState<{type: 'error' | 'success', message: string} | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setFormFeedback(null);
    try {
      const [schoolsData, adminsData, teachersData] = await Promise.all([
        api.get<School[]>('/api/v1/superadmin/schools'),
        api.get<User[]>('/api/v1/superadmin/admins'),
        api.get<TeacherAccount[]>('/api/v1/superadmin/teachers'),
      ]);

      setSchools(schoolsData);
      setAdmins(adminsData);
      setTeachers(teachersData);

      if (schoolsData.length > 0 && !newAdminSchoolId) {
          setNewAdminSchoolId(schoolsData[0].school_id);
      }
    } catch (e) {
      if (e instanceof Error) setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormFeedback(null);
    if (!newSchoolName) {
      setFormFeedback({type: 'error', message: 'School name cannot be empty.'});
      return;
    }
    try {
        const newSchool = await api.post<School>('/api/v1/superadmin/schools', { school_name: newSchoolName });
        setSchools(prev => [...prev, newSchool].sort((a, b) => a.school_name.localeCompare(b.school_name)));
        setNewSchoolName("");
        setFormFeedback({type: 'success', message: 'School created successfully!'});
    } catch(err) {
        if(err instanceof Error) setFormFeedback({type: 'error', message: err.message});
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormFeedback(null);
    if (!newAdminFirstName || !newAdminLastName || !newAdminEmail || !newAdminPassword || !newAdminSchoolId) {
       setFormFeedback({type: 'error', message: 'All admin fields are required.'});
       return;
    }
     try {
        const newAdmin = await api.post<User>('/api/v1/superadmin/admins', {
            first_name: newAdminFirstName,
            last_name: newAdminLastName,
            email: newAdminEmail,
            password: newAdminPassword,
            school_id: newAdminSchoolId
        });

        setAdmins(prev => [...prev, newAdmin].sort((a,b) => a.last_name.localeCompare(b.last_name)));
        setNewAdminFirstName("");
        setNewAdminLastName("");
        setNewAdminEmail("");
        setNewAdminPassword("");
        setFormFeedback({type: 'success', message: 'Admin created successfully!'});
    } catch(err) {
        if(err instanceof Error) setFormFeedback({type: 'error', message: err.message});
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!window.confirm("Are you sure you want to permanently delete this admin? This action cannot be undone.")) return;
    setFormFeedback(null);
    try {
        await api.delete(`/api/v1/superadmin/admins/${adminId}`);
        setAdmins(prev => prev.filter(admin => admin.user_id !== adminId));
        setFormFeedback({type: 'success', message: 'Admin deleted successfully.'});
    } catch(err) {
        if(err instanceof Error) setFormFeedback({type: 'error', message: err.message});
    }
  };

  const handleResetAdminPassword = async (userId: string) => {
    if (!window.confirm("Are you sure you want to reset this admin's password?")) return;
    setFormFeedback(null);
     try {
        const data = await api.post<GeneratedCredential>('/api/v1/superadmin/reset-admin-password', { userId });
        setResetCredential(data);
    } catch(err) {
        if(err instanceof Error) setFormFeedback({type: 'error', message: err.message});
    }
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setFormFeedback(null);
    try {
      if (itemToDelete.type === 'school') {
        const school = itemToDelete.data as School;
        await api.delete(`/api/v1/superadmin/schools/${school.school_id}`);
        setSchools(prev => prev.filter(s => s.school_id !== school.school_id));
        setFormFeedback({ type: 'success', message: `School '${school.school_name}' and all its data have been deleted.` });
      } else if (itemToDelete.type === 'teacher') {
        // FIX: Cast itemToDelete.data to TeacherAccount to safely access user-specific properties.
        const teacher = itemToDelete.data as TeacherAccount;
        await api.delete(`/api/v1/superadmin/teachers/${teacher.user_id}`);
        setTeachers(prev => prev.filter(t => t.user_id !== teacher.user_id));
        setFormFeedback({ type: 'success', message: `Teacher '${teacher.first_name} ${teacher.last_name}' has been deleted.` });
      }
    } catch (err) {
      if (err instanceof Error) setFormFeedback({ type: 'error', message: err.message });
    } finally {
      setItemToDelete(null);
    }
  };


  if (loading) return <LoadingSpinner message="Loading Super Admin data..." />;
  if (error) return <div className="text-red-500 bg-red-100 p-3 rounded-md text-center">{error}</div>;

  const TABS: { key: typeof tab; label: string; icon: string }[] = [
    { key: 'schools', label: 'Manage Schools', icon: '🏫' },
    { key: 'admins', label: 'Manage Admins', icon: '🛡️' },
    { key: 'teachers', label: 'Manage Teachers', icon: '🍎' },
    { key: 'audit', label: 'Audit Log', icon: '📜' },
  ];

  return (
    <div className="w-full max-w-7xl">
        {resetCredential && <ResetPasswordConfirmationModal credential={resetCredential} onClose={() => setResetCredential(null)} />}
        {itemToDelete && (
          <ConfirmationModal
            title={`Delete ${itemToDelete.type === 'school' ? 'School' : 'Teacher'}`}
            message={
              itemToDelete.type === 'school'
                ? `Are you sure you want to permanently delete "${(itemToDelete.data as School).school_name}"? This is irreversible and will also delete all associated teachers, students, and their data.`
                // FIX: Cast itemToDelete.data to TeacherAccount to safely access first_name and last_name.
                : `Are you sure you want to permanently delete the teacher "${(itemToDelete.data as TeacherAccount).first_name} ${(itemToDelete.data as TeacherAccount).last_name}"? This action cannot be undone.`
            }
            onConfirm={handleConfirmDelete}
            onCancel={() => setItemToDelete(null)}
            confirmText="Delete Permanently"
            confirmColor="red"
          />
        )}

        <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-3xl shadow-md">👑</div>
            <div>
                <h2 className="text-3xl font-bold text-gray-800">Super Admin Dashboard</h2>
                <span className="text-sm text-gray-500">Manage schools, admins, and teachers platform-wide</span>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <AdminStatCard icon="🏫" color="blue" label="Schools" value={String(schools.length)} description="Registered on the platform" />
            <AdminStatCard icon="🛡️" color="purple" label="School Admins" value={String(admins.length)} description="Across all schools" />
            <AdminStatCard icon="🍎" color="green" label="Teachers" value={String(teachers.length)} description="Across all schools" />
        </div>

        {formFeedback && (
             <div className={`p-3 mb-4 rounded-md text-center ${formFeedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {formFeedback.message}
            </div>
        )}

        <div className="flex border-b border-gray-300 mb-6 flex-wrap">
            {TABS.map(t => (
                <button
                    key={t.key}
                    className={`py-2 px-6 font-semibold flex items-center gap-1.5 ${tab === t.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setTab(t.key)}
                >
                    <span>{t.icon}</span> {t.label}
                </button>
            ))}
        </div>

        {tab === 'schools' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <form onSubmit={handleCreateSchool} className="bg-white rounded-xl shadow-md border border-gray-100 p-6 h-fit">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><span className="text-xl">➕</span> Create New School</h3>
                    <div className="mb-4">
                        <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700">School Name</label>
                        <input type="text" id="schoolName" value={newSchoolName} onChange={e => setNewSchoolName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500" required />
                    </div>
                    <div className="flex justify-end">
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-semibold">Create School</button>
                    </div>
                </form>
                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                        <span className="text-xl">🏫</span>
                        <h3 className="text-lg font-semibold text-gray-800">Existing Schools</h3>
                        <span className="ml-auto text-sm text-gray-400">{schools.length} total</span>
                    </div>
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0"><tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School Name</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr></thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {schools.length > 0 ? schools.map(school => <tr key={school.school_id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{school.school_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => setItemToDelete({ type: 'school', data: school })} className="text-red-600 hover:text-red-900">Delete</button>
                                    </td>
                                </tr>) : (
                                    <tr><td colSpan={2} className="p-0"><EmptyState icon="🏫" message="No schools yet -- create one to get started." /></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

        {tab === 'admins' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                 <form onSubmit={handleCreateAdmin} className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><span className="text-xl">➕</span> Create New School Admin</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">First Name</label>
                            <input type="text" value={newAdminFirstName} onChange={e => setNewAdminFirstName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Last Name</label>
                            <input type="text" value={newAdminLastName} onChange={e => setNewAdminLastName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500" required />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input type="email" value={newAdminEmail} onChange={e => setNewAdminEmail(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Temporary Password</label>
                        <input type="password" value={newAdminPassword} onChange={e => setNewAdminPassword(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">School</label>
                        <select value={newAdminSchoolId} onChange={e => setNewAdminSchoolId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md bg-white focus:border-blue-500 focus:ring-blue-500" required disabled={schools.length === 0}>
                            {schools.length === 0 ? <option>Create a school first</option> : schools.map(s => <option key={s.school_id} value={s.school_id}>{s.school_name}</option>)}
                        </select>
                    </div>
                    <div className="flex justify-end">
                        <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold" disabled={schools.length === 0}>Create Admin</button>
                    </div>
                </form>

                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                        <span className="text-xl">🛡️</span>
                        <h3 className="text-lg font-semibold text-gray-800">Existing Admins</h3>
                        <span className="ml-auto text-sm text-gray-400">{admins.length} total</span>
                    </div>
                    <div className="overflow-x-auto max-h-[450px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0"><tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr></thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {admins.length > 0 ? admins.map(admin => (
                                    <tr key={admin.user_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <div className="flex items-center gap-3">
                                                <Avatar a={admin.first_name} b={admin.last_name} color="bg-indigo-500" />
                                                <div>
                                                    {admin.first_name} {admin.last_name}
                                                    <span className="block text-xs text-gray-500">{admin.email}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.school_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button onClick={() => handleResetAdminPassword(admin.user_id)} className="text-blue-600 hover:text-blue-900">Reset Pass</button>
                                            <button onClick={() => handleDeleteAdmin(admin.user_id)} className="text-red-600 hover:text-red-900">Delete</button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={3} className="p-0"><EmptyState icon="🛡️" message="No school admins yet." /></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}

        {tab === 'teachers' && (
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                    <span className="text-xl">🍎</span>
                    <h3 className="text-lg font-semibold text-gray-800">All Teachers</h3>
                    <span className="ml-auto text-sm text-gray-400">{teachers.length} total</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {teachers.length > 0 ? teachers.map(teacher => (
                                <tr key={teacher.user_id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        <div className="flex items-center gap-3">
                                            <Avatar a={teacher.first_name} b={teacher.last_name} color="bg-green-500" />
                                            {teacher.first_name} {teacher.last_name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.email}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{teacher.school_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${teacher.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {teacher.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => setItemToDelete({ type: 'teacher', data: teacher })} className="text-red-600 hover:text-red-900">Delete</button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="p-0"><EmptyState icon="🍎" message="No teachers found on the platform yet." /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        {tab === 'audit' && <AuditLogViewer />}
    </div>
  );
};

export default SuperAdminDashboard;
